// Click-to-open help tip. Use anywhere we want an "idiot-proof" inline
// explanation next to a label, button, or section header. Differs from
// `info-tip.tsx` (which is a hover tooltip and useless on touch devices) —
// this is a Popover so the explanation persists until the user dismisses it.
//
// Theme-aware: the popover surface uses bg-[#1A1A1A] which the banker /
// investor light-mode CSS overrides remap to a readable light card.

import { HelpCircle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

interface HelpTipProps {
  /** What this thing is, in plain English. Keep it short — 1–3 sentences. */
  content: React.ReactNode;
  /** Optional title shown above the content. */
  title?: string;
  /** Tooltip side. Default "top". */
  side?: "top" | "right" | "bottom" | "left";
  /** Tighter icon for inline use next to a label. */
  size?: "xs" | "sm";
  className?: string;
}

export default function HelpTip({ content, title, side = "top", size = "xs", className = "" }: HelpTipProps) {
  const iconSize = size === "xs" ? "w-3.5 h-3.5" : "w-4 h-4";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={title || "What is this?"}
          className={`inline-flex items-center justify-center rounded-full text-white/35 hover:text-white/80 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF5C00]/40 ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          <HelpCircle className={iconSize} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align="start"
        sideOffset={6}
        className="max-w-[320px] bg-[#1A1A1A] border border-white/10 text-white text-xs leading-relaxed px-3 py-2.5 rounded-lg shadow-xl"
      >
        {title && <p className="text-white text-[11px] font-mono uppercase tracking-wider mb-1.5 opacity-80">{title}</p>}
        <div className="text-white/85">{content}</div>
      </PopoverContent>
    </Popover>
  );
}
